// client/src/components/pages/LexiStar/RecomputeJobs/DiffTable.jsx
//
// Before/after diff table (LS-09 §5.19, §8 "before/after diff table"): one
// block per changed record — change marker, table and subject (trip link when
// there is one) — then its fields with the old value beside the new. Fields in
// `fields_changed` are highlighted and listed first; unchanged fields fold away
// behind "show all fields". Changed cells carry a ● marker as well as the tint
// so the change never depends on colour alone.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CHANGE_META, TABLE_LABELS, diffFields, formatDiffValue } from './recomputeLogic';

function ChangeBadge({ change }) {
  const meta = CHANGE_META[change] || CHANGE_META.changed;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ${meta.className}`}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

function DiffRecord({ row }) {
  const [all, setAll] = useState(false);
  const fields = diffFields(row);
  const changed = fields.filter((f) => f.changed);
  const shown = all || row.change !== 'changed' ? fields : changed;
  const hidden = fields.length - shown.length;
  return (
    <li
      className="py-3"
      data-change={row.change}
      aria-label={`${row.change} ${row.table_name} ${row.subject_id}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <ChangeBadge change={row.change} />
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {TABLE_LABELS[row.table_name] || row.table_name}
        </span>
        <span className="font-mono text-xs text-slate-800 break-all">{row.subject_id}</span>
        {row.trip_id && (
          <Link
            to={`/lexistar/trips/${row.trip_id}`}
            className="text-xs text-primary-700 hover:underline"
          >
            Open trip
          </Link>
        )}
        {row.change === 'changed' && (
          <span className="text-xs text-slate-500 tabular-nums">
            {changed.length} field{changed.length === 1 ? '' : 's'} changed
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label={`${row.table_name} ${row.subject_id}`}>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="py-1 pr-3 font-semibold w-1/4">Field</th>
              <th className="py-1 pr-3 font-semibold">Before</th>
              <th className="py-1 font-semibold">After</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((f) => (
              <tr
                key={f.field}
                className={f.changed ? 'bg-amber-50' : ''}
                data-changed={f.changed ? 'true' : 'false'}
              >
                <td className="py-1 pr-3 font-mono text-xs text-slate-700 align-top">
                  {f.changed && (
                    <span className="text-amber-700 mr-1" aria-label="changed">
                      ●
                    </span>
                  )}
                  {f.field}
                </td>
                <td
                  className={`py-1 pr-3 tabular-nums align-top break-words ${f.changed ? 'text-slate-500 line-through decoration-slate-400' : 'text-slate-700'}`}
                >
                  {row.change === 'added' ? '—' : formatDiffValue(f.before)}
                </td>
                <td
                  className={`py-1 tabular-nums align-top break-words ${f.changed ? 'text-slate-900 font-semibold' : 'text-slate-700'}`}
                >
                  {row.change === 'removed' ? '—' : formatDiffValue(f.after)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {row.change === 'changed' && fields.length > changed.length && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="mt-1 text-xs font-medium text-primary-700 hover:underline"
        >
          {all ? 'Show changed fields only' : `Show all fields (${hidden} unchanged)`}
        </button>
      )}
    </li>
  );
}

export default function DiffTable({ rows, emptyText = 'No changed records in this view.' }) {
  if (!rows?.length) return <p className="text-sm text-slate-500 py-4">{emptyText}</p>;
  return (
    <ul className="divide-y divide-slate-100" aria-label="Changed records">
      {rows.map((r, i) => (
        <DiffRecord key={r.id ?? `${r.table_name}:${r.subject_id}:${i}`} row={r} />
      ))}
    </ul>
  );
}
